import clsx from 'clsx';
import React, { ReactElement } from 'react';
import styled, { css } from 'styled-components';
import { COLORS } from '../../theme/utils/colors';

export interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  isValid?: boolean;
  hasRightNeighbor?: boolean;
  hasLeftNeighbor?: boolean;
  innerRef?: React.Ref<HTMLInputElement>;
  adornmentValueLeft?: string | ReactElement;
  adornmentValueRight?: string | ReactElement;
  shouldValidate?: boolean;
}

const InputComponent: React.FC<Props> = ({
  isValid = false,
  hasRightNeighbor = false,
  hasLeftNeighbor = false,
  shouldValidate = false,
  adornmentValueRight,
  adornmentValueLeft,
  innerRef,
  ...rest
}) => {
  return (
    <InputWrapper>
      {adornmentValueLeft && (
        <AppendValueWrapper $position="left">
          <AppendValue>{adornmentValueLeft}</AppendValue>
        </AppendValueWrapper>
      )}
      <StyledInput
        className={clsx('outline-none', {
          'border border-gray-200': !shouldValidate,
          'border-2 border-status-error': !isValid && shouldValidate,
          'border-2 border-black': isValid && shouldValidate,
        })}
        $hasRightNeighbor={hasRightNeighbor}
        $hasLeftNeighbor={hasLeftNeighbor}
        $needsSpacingRight={!!adornmentValueRight}
        $needsSpacingLeft={!!adornmentValueLeft}
        $isValid={isValid}
        aria-invalid={shouldValidate && !isValid}
        ref={innerRef}
        {...rest}
      />
      {adornmentValueRight && (
        <AppendValueWrapper $position="right">
          <AppendValue>{adornmentValueRight}</AppendValue>
        </AppendValueWrapper>
      )}
    </InputWrapper>
  );
};

export const Input = React.forwardRef<HTMLInputElement, Props>((props, ref) => (
  <InputComponent {...props} innerRef={ref} />
));

export const formInputFocusStyle = css`
  border: var(--outline-size) var(--outline-style) var(--outline-color);
  outline: none;
`;

const BORDER_RADIUS = '8px';

const InputWrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
`;

const AppendValueWrapper = styled.div<{ $position: 'left' | 'right' }>`
  position: absolute;
  right: 0px;
  ${(props) =>
    props.$position === 'left' &&
    css`
      left: 0px;
    `};

  ${(props) =>
    props.$position === 'right' &&
    css`
      right: 0px;
    `};
  top: 0px;
  padding: 0 0.625rem;
  display: flex;
  height: 100%;
  align-items: center;
  max-width: 40px;
  font-weight: ${({ theme }) => theme.fontWeights.default};
`;

const AppendValue = styled.span`
  overflow: hidden;
`;

const StyledInput = styled.input<{
  $isValid: boolean;
  $hasRightNeighbor: boolean;
  $hasLeftNeighbor: boolean;
  $needsSpacingRight: boolean;
  $needsSpacingLeft: boolean;
}>`
  background: ${COLORS.WHITE};
  color: ${COLORS.TEXT};
  height: 2.813rem;
  width: 100%;
  font-size: 1rem;
  padding: 0 1.375rem;
  ${(props) =>
    props.$needsSpacingLeft &&
    css`
      padding-left: 1.875rem;
    `};

  ${(props) =>
    props.$needsSpacingRight &&
    css`
      padding-right: 1.875rem;
    `};

  border-top-left-radius: ${({ $hasLeftNeighbor }) => ($hasLeftNeighbor ? '0' : BORDER_RADIUS)};
  border-bottom-left-radius: ${({ $hasLeftNeighbor }) => ($hasLeftNeighbor ? '0' : BORDER_RADIUS)};

  border-top-right-radius: ${({ $hasRightNeighbor }) => ($hasRightNeighbor ? '0' : BORDER_RADIUS)};
  border-bottom-right-radius: ${({ $hasRightNeighbor }) =>
    $hasRightNeighbor ? '0' : BORDER_RADIUS};

  &:disabled {
    border: 1px solid ${COLORS.GRAY};
    outline: none;
  }

  &:focus-visible,
  &:focus {
    ${formInputFocusStyle};
  }
`;
